import React, { useCallback, useMemo, useState } from 'react';
import { X, Loader2, Trash2 } from 'lucide-react';
import type { SecurityCommandControlLogEntry, SecurityCommandControlLogIncident } from '../services/api';
import { securityCommandControlLogApi } from '../services/api';
import { appConfirm } from '../context/SystemDialogContext';

export interface SecurityCommandControlLogBulkDeleteModalProps {
  open: boolean;
  onClose: () => void;
  entry: SecurityCommandControlLogEntry | null;
  onDeleted?: () => void;
}

function incidentLabel(i: SecurityCommandControlLogIncident) {
  const bits = [
    i.incidentNo ? `#${i.incidentNo}` : '',
    i.incidentTime ? i.incidentTime : '',
    i.location ? i.location : '',
  ].filter(Boolean);
  return bits.join(' · ') || `Incident ${i.id}`;
}

export const SecurityCommandControlLogBulkDeleteModal: React.FC<SecurityCommandControlLogBulkDeleteModalProps> = ({
  open,
  onClose,
  entry,
  onDeleted,
}) => {
  const entryId = entry?.id ?? null;
  const incidents = entry?.incidents || [];
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sorted = useMemo(() => [...incidents].sort((a, b) => a.id - b.id), [incidents]);

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAll = () => setSelectedIds(new Set(sorted.map((i) => i.id)));
  const clearAll = () => setSelectedIds(new Set());

  const handleDelete = useCallback(async () => {
    if (!entryId) return;
    if (selectedIds.size === 0) return;
    if (
      !(await appConfirm(
        `Permanently delete ${selectedIds.size} incident(s) from this entry? This cannot be undone.`,
        { danger: true, confirmLabel: 'Delete' }
      ))
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await securityCommandControlLogApi.bulkDeleteIncidents(entryId, Array.from(selectedIds));
      onDeleted?.();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  }, [entryId, onClose, onDeleted, selectedIds]);

  if (!open || !entry) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Bulk delete incidents</h2>
            <p className="mt-0.5 truncate text-xs text-gray-500" title={entry.event}>
              Entry #{entry.id} · {entry.logDate} · {entry.event}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
          {incidents.length === 0 ? (
            <div className="text-sm text-gray-600">No incidents to delete.</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-gray-700">
                  Selected: <span className="font-semibold">{selectedIds.size}</span> / {incidents.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                    onClick={selectAll}
                    disabled={deleting}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                    onClick={clearAll}
                    disabled={deleting}
                  >
                    Clear
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {error}
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-y-auto rounded border border-gray-200">
                <ul className="divide-y divide-gray-100">
                  {sorted.map((i) => (
                    <li key={i.id} className="flex items-start gap-3 px-3 py-2 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selectedIds.has(i.id)}
                        onChange={() => toggle(i.id)}
                        disabled={deleting}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">{incidentLabel(i)}</div>
                        {i.details ? (
                          <div className="mt-0.5 text-xs text-gray-600 line-clamp-2">{i.details}</div>
                        ) : null}
                        {(i.attachments || []).length > 0 ? (
                          <div className="mt-1 text-xs text-gray-500">
                            Attachments: {(i.attachments || []).length}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50"
            onClick={onClose}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            onClick={() => void handleDelete()}
            disabled={deleting || selectedIds.size === 0 || incidents.length === 0}
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete selected
          </button>
        </div>
      </div>
    </div>
  );
};

